import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Ambulance, Phone, Loader2, CheckCircle2, MapPin, Building2, User, Clock, XCircle, ShieldCheck,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { emergencyApi } from "@/lib/api";

const STEPS = [
  { key: "CREATED", label: "Request received" },
  { key: "ASSIGNED", label: "Provider assigned" },
  { key: "EN_ROUTE", label: "On the way" },
  { key: "ARRIVED", label: "Arrived" },
  { key: "COMPLETED", label: "Completed" },
];
// Map various backend statuses onto the 5-step tracker.
const STEP_INDEX = {
  CREATED: 0, PENDING: 0, SEARCHING: 0,
  ASSIGNED: 1, ACCEPTED: 1, CONFIRMED: 1, DISPATCHED: 1,
  EN_ROUTE: 2, ENROUTE: 2, ON_THE_WAY: 2, IN_TRANSIT: 2,
  ARRIVED: 3, REACHED: 3, AT_PICKUP: 3,
  COMPLETED: 4, RESOLVED: 4, CLOSED: 4,
};

function fmtTime(ts) {
  try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch (e) { return ""; }
}

export default function CaseStatus() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [providers, setProviders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.allSettled([emergencyApi.getCase(id), emergencyApi.caseProviders(id)]);
      if (c.status === "fulfilled") setCaseData(c.value.data);
      if (p.status === "fulfilled") setProviders(p.value.data);
    } catch (e) { /* ignore transient */ } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [load]);

  const status = caseData?.status || "CREATED";
  const stepIdx = STEP_INDEX[status] ?? 0;
  const isCancelled = /CANCEL/i.test(status);
  const isAttention = /NO_NEARBY|NO_HOSPITAL|FAILED|UNAVAILABLE/i.test(status);
  const isDone = stepIdx >= 4 || isCancelled;
  const paramedic = providers?.paramedic;
  const ambulance = providers?.ambulance;

  const cancel = async () => {
    setCancelling(true);
    try {
      await emergencyApi.cancelCase(id);
      toast.success("Request cancelled");
      await load();
    } catch (e) {
      toast.error("Could not cancel. Please call 112 if this is urgent.");
    } finally {
      setCancelling(false);
    }
  };

  if (loading && !caseData) {
    return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="case-status-page">
      {/* Hero */}
      <Card className={`rounded-3xl p-6 border-border ${isCancelled ? "bg-muted" : "bg-destructive/5"}`}>
        <div className="flex items-center gap-4">
          <div className={`h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 ${isCancelled ? "bg-muted-foreground/10" : "bg-destructive/15"}`}>
            {isCancelled ? <XCircle className="h-7 w-7 text-muted-foreground" /> : isDone ? <CheckCircle2 className="h-7 w-7 text-[#16A34A]" /> : <Ambulance className="h-7 w-7 text-destructive animate-pulse" />}
          </div>
          <div className="min-w-0">
            <p className="text-lg font-bold">
              {isCancelled ? "Request cancelled" : isAttention ? "Still finding a provider" : isDone ? "Service completed" : "Help is on the way"}
            </p>
            <p className="text-sm text-muted-foreground truncate">
              {(caseData?.reason || "Emergency request").replace(/^🚨\s*/, "")}
            </p>
            <span className="inline-block mt-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-card border border-border" data-testid="case-status-badge">{status}</span>
          </div>
        </div>
        {isAttention && (
          <p className="mt-3 text-sm text-[#B45309] bg-[#FEF3C7] rounded-xl px-3 py-2" data-testid="case-attention">
            We're still locating a nearby provider for your request. If this is life-threatening, please call 112 now.
          </p>
        )}
      </Card>

      {/* Stepper */}
      {!isCancelled && (
        <Card className="rounded-2xl p-5 border-border mt-4">
          <div className="space-y-4">
            {STEPS.map((s, i) => {
              const done = i < stepIdx;
              const active = i === stepIdx;
              return (
                <div key={s.key} className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-[#16A34A]" : active ? "bg-destructive" : "bg-muted"}`}>
                    {done ? <CheckCircle2 className="h-4 w-4 text-white" /> : <span className={`text-xs font-bold ${active ? "text-white" : "text-muted-foreground"}`}>{i + 1}</span>}
                  </div>
                  <span className={`text-sm ${done || active ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                  {active && !isDone && <Loader2 className="h-3.5 w-3.5 animate-spin text-destructive ml-auto" />}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Assigned provider */}
      {(paramedic || ambulance) && (
        <Card className="rounded-2xl p-4 border-border mt-4" data-testid="case-provider">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Assigned provider</p>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center"><User className="h-5 w-5 text-primary" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{paramedic?.name || ambulance?.driver_name || ambulance?.vehicle_number || "Provider"}</p>
              <p className="text-sm text-muted-foreground">{ambulance?.vehicle_number || paramedic?.phone || "Assigned"}</p>
            </div>
            {(paramedic?.phone || ambulance?.driver_phone) && (
              <a href={`tel:${paramedic?.phone || ambulance?.driver_phone}`} className="h-10 w-10 rounded-xl bg-[#16A34A]/10 flex items-center justify-center"><Phone className="h-4 w-4 text-[#16A34A]" /></a>
            )}
          </div>
        </Card>
      )}

      {/* Location & hospital */}
      <Card className="rounded-2xl p-4 border-border mt-4 space-y-3">
        {caseData?.location && (
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold">Pickup location</p>
              <p className="text-xs text-muted-foreground">{caseData.location.address || `${Number(caseData.location.lat).toFixed(4)}, ${Number(caseData.location.lng).toFixed(4)}`}</p>
            </div>
          </div>
        )}
        {caseData?.hospital_id && (
          <div className="flex items-start gap-3">
            <Building2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div><p className="text-sm font-semibold">Destination hospital</p><p className="text-xs text-muted-foreground">Assigned</p></div>
          </div>
        )}
      </Card>

      {/* Timeline */}
      {Array.isArray(caseData?.timeline) && caseData.timeline.length > 0 && (
        <Card className="rounded-2xl p-4 border-border mt-4">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-3">Activity</p>
          <div className="space-y-3">
            {caseData.timeline.slice().reverse().map((t, i) => (
              <div key={i} className="flex items-start gap-3">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm">{t.description}</p>
                  <p className="text-xs text-muted-foreground">{fmtTime(t.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Actions */}
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <a href="tel:112" className="flex-1"><Button className="w-full h-12 rounded-2xl bg-destructive text-destructive-foreground" data-testid="case-call-112"><Phone className="h-4 w-4 mr-1.5" /> Call 112</Button></a>
        {!isDone && (
          <Button variant="outline" onClick={cancel} disabled={cancelling} className="flex-1 h-12 rounded-2xl" data-testid="case-cancel">
            {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel Request"}
          </Button>
        )}
        {isDone && (
          <Button variant="outline" onClick={() => navigate("/home")} className="flex-1 h-12 rounded-2xl" data-testid="case-done-home">Back to Home</Button>
        )}
      </div>

      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-4">
        <ShieldCheck className="h-3.5 w-3.5" /> Live status • auto-refreshes every few seconds
      </p>
    </div>
  );
}
